<script>
  import { format } from 'svelte-i18n';
  import { Tooltip } from '@sveltestrap/sveltestrap';

  import { Icon } from '@client/components/icon';

  import { generateActivityStats } from '@client/utils/activity';

  import { generateUUID } from '@common/utils/strings';

  /**
   * @typedef {Object} ActivityStat
   * @property {number} count - Total number of occurrences of this activity type.
   * @property {string} color - Background color to render the icon.
   * @property {string} icon - Icon class name to use.
   * @property {string} iconTooltip - Translation key for tooltip label.
   */

  /**
   * @typedef {Object.<string, ActivityStat>} ActivityStatsMap
   */

  /**
   * The activity event data.
   * @type {Object}
   */
  export let data = {};

  /**
   * Array of activity event types to show (e.g., 'opened', 'clicked').
   * If empty, all events will be shown.
   * @type {string[]}
   */
  export let showOnly = [];

  /**
   * Whether to show tooltips on hover.
   * @type {boolean}
   */
  export let showTooltip = false;

  /**
   * Whether to show counts next to each event icon.
   * @type {boolean}
   */
  export let showCounts = false;

  /**
   * Unique ID for tooltips.
   * @type {string}
   */
  const id = generateUUID();

  /**
   * Computed activity statistics per event type.
   * @type {ActivityStatsMap}
   */
  $: stats = generateActivityStats(data);

  /**
   * Determines if the email was likely scanned by a bot (previewed/rendered without opening).
   * @type {boolean}
   */
  $: scanner = stats
    ? stats.viewed.count === 0 &&
      (stats.adclick.count > 0 || stats.linkclick.count > 0 || stats.viewerlaunched.count > 0 || stats.downloaded.count)
    : false;
</script>

<div class="events">
  {#each Object.entries(stats) as [value, config]}
    {#if !showOnly.length || showOnly.includes(value)}
      {#if config.count}
        <div class="event" style="background-color: {config.color}" class:help={showTooltip}>
          <i id="tooltip-{value}-{id}" class={config.icon} />

          {#if showTooltip}
            <Tooltip target="tooltip-{value}-{id}">
              {$format(config.iconTooltip)}
            </Tooltip>
          {/if}
        </div>

        {#if showCounts}
          {config.count}
        {/if}
      {/if}
    {/if}
  {/each}

  {#if !showOnly.length}
    {#if scanner}
      <div class="event" style="background-color: var(--color-red-400);" class:help={showTooltip}>
        <Icon name="alert-triangle" id="tooltip-scanner-{id}" />

        {#if showTooltip}
          <Tooltip target="tooltip-scanner-{id}">
            {$format('label.TOOLTIP.ACTIVITY_EVENT_SCANNER_VIEW')}
          </Tooltip>
        {/if}
      </div>
    {/if}
  {/if}
</div>

<style lang="scss" src="./activity-event-icons.scss"></style>
